import React from 'react';
import { useTranslation } from 'react-i18next';

import { CreateDevice } from '@/console/features/devices/components/create-device/create-device';
import { AddDevice } from '@/console/features/devices/components/device-drawer/add-device';
import { DevicesIcon } from '@/shared/assets/icons/devices-icon';
import { MultiDrawer } from '@/shared/components/ui/multi-drawer/multi-drawer';
import {
  TCustomMultiDrawerProps,
  TMultiDrawerTab,
} from '@/shared/components/ui/multi-drawer/multi-drawer.types';
import { usePermissionCheck } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';

export const DeviceDrawer: React.FC<TCustomMultiDrawerProps> = ({
  assetId,
  disableTabs,
  ...rest
}) => {
  const { t } = useTranslation();
  const { can } = usePermissionCheck();
  const hasUpdatePermission = can('device:updateDevice', assetId);
  const hasCreatePermission = can('device:createDevice', assetId);

  const addDeviceTab: TMultiDrawerTab = {
    title: t('device:select_existing_devices'),
    disabled: !assetId,
    content: (handleClose) => (
      <AddDevice groupId={assetId!} handlePopoverClose={handleClose} />
    ),
  };

  const createDeviceTab: TMultiDrawerTab = {
    title: t('device:create_device'),
    content: (handleClose, isOpen) => (
      <CreateDevice
        groupId={assetId!}
        handleClose={handleClose}
        isOpen={isOpen}
      />
    ),
  };

  const getTabs = () => {
    const tabs: TMultiDrawerTab[] = [];
    if (assetId && hasUpdatePermission) {
      tabs.push(addDeviceTab);
    }
    if (hasCreatePermission) {
      tabs.push(createDeviceTab);
    }
    return tabs;
  };

  const tabs = getTabs();

  if (tabs.length === 0) {
    return null;
  }

  return (
    <MultiDrawer
      title={t('device:add_device')}
      tabs={tabs}
      componentLabel={t('device:add_device')}
      defaultTab={0}
      disableTabs={disableTabs || tabs.length === 1}
      icon={<DevicesIcon />}
      tabsFullWidth
      {...rest}
    />
  );
};
